import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import React from 'react';
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, AlertTitle, Alert, Divider } from '@mui/material';
import { t } from 'i18next';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import { ControlledFileDropZone } from 'src/shared/components/form/fileDropZone';
import { importFiles } from 'src/shared/services/incomingBankPaymanetService';
import { FileValidationModeEnum } from 'src/shared/functions/fileExtensions';
import { IncomingBankPaymentImportRequest } from '../models/incomingBankPaymentImportRequest';

type IncomingBankPaymentImportModalProps = {
  modalOpen: boolean;
  onClose: () => void;
};

export const IncomingBankPaymentImportModal = (props: IncomingBankPaymentImportModalProps) => {
  const { modalOpen, onClose } = props;

  const componentId = `dialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  const IncomingBankPaymentImportRequestSchema: Schema<IncomingBankPaymentImportRequest> = yup.object().shape({
    files: yup.mixed<File[]>().optional().nullable().notRequired(),
  });

  const formMethods = useForm<IncomingBankPaymentImportRequest>({
    resolver: yupResolver(IncomingBankPaymentImportRequestSchema),
    mode: 'all',
    defaultValues: {
      files: [],
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = React.useCallback(
    async (data) => {
      ConsoleHelper.log('onSubmit', data);
      try {
        await importFiles(data);
        toast.success(t('invoice:incomingBankPayment.importSuccess'));
        onHandleClose();
      } catch (e) {
        ConsoleHelper.error('saveFormDataError:', e);
      }
    },
    [onHandleClose],
  );

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dialog
          onClose={onHandleClose}
          aria-labelledby={`${componentId}-titel`}
          open={modalOpen}
          maxWidth='sm'
          fullWidth
          sx={{
            minWidth: {
              xs: '90vw',
              md: '400px',
            },
            '& .MuiDialogContent-root': {
              px: 0,
              pt: 0,
              pb: 2,
            },
            '& .MuiDialogActions-root': {
              p: 1,
            },
          }}
        >
          <DialogTitle sx={{ m: 0, py: 1.5, px: 2 }} id={`${componentId}-titel`}>
            {t('invoice:incomingBankPayment.importModalHeader')}
            <DialogCloseButton handleClose={onHandleClose} />
          </DialogTitle>
          <DialogContent dividers>
            <Alert severity='info' sx={{ borderRadius: 0 }}>
              <AlertTitle>{t('invoice:incomingBankPayment.fileFormatHeader')}</AlertTitle>
              {t('invoice:incomingBankPayment.fileFormatDescription')}
            </Alert>
            <Divider />
            <Box sx={{ px: 0.5 }}>
              <ControlledFileDropZone
                name='files'
                disabled={false}
                fileValidationMode={FileValidationModeEnum.IncomingBankPayment}
                showFileHeader={false}
                documentFiles={undefined}
                orientation='vertical'
                multiple
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose}>
                {t('common:button.cancel')}
              </Button>
              <Box>
                <Button variant='contained' color='error' disableElevation onClick={onHandleClose} sx={{ mr: 1 }}>
                  {t('common:button.undoImport')}
                </Button>
                <LoadingButton autoFocus loading={isSubmitting} variant='contained' type='submit' disableElevation onClick={onHandleSave}>
                  {t('common:button.import')}
                </LoadingButton>
              </Box>
            </Box>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
};
